import { inject, Injectable } from "@angular/core";
import { OAuthService, OAuthSuccessEvent } from "angular-oauth2-oidc";
import { TokenService } from "@auth/services/token/token.service";
import { ApiService } from "@api/services/api.service";
import { Observable, Subject } from "rxjs";
import { SessionStorageService } from "@shared/services/session-storage.service";
import { LoaderService } from "@shared/services/loader/loader.service";
import { SessionStorageKeys } from "@shared/services/static/session-storage-keys";
import { AuthConfigService } from "@auth/services/auth-config.service";

@Injectable({
    providedIn: "root",
})
export class AuthService {
    private readonly oauthService = inject(OAuthService);
    private readonly loaderService = inject(LoaderService);
    private readonly tokenService = inject(TokenService);
    private readonly apiService = inject(ApiService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly authConfig = inject(AuthConfigService).authConfig;

    private readonly authenticationCompleted = new Subject<void>();

    public authenticationCompleted$ = this.authenticationCompleted.asObservable();

    public notifyAuthenticationComplete(): void {
        this.authenticationCompleted.next();
    }

    public initialize(): Promise<OAuthSuccessEvent> {
        this.oauthService.configure(this.authConfig());
        this.oauthService.setupAutomaticSilentRefresh();
        return this.oauthService.loadDiscoveryDocument();
    }

    public getClientAuthJwt(): Observable<string> {
        const authCode = this.sessionStorageService.getItem(SessionStorageKeys.AuthCode);
        const query = authCode ? `?code=${authCode}` : "";

        return this.apiService.get<string>(`api/auth/client-assertion${query}`);
    }

    public isAuthorized(): boolean {
        return this.oauthService.hasValidAccessToken();
    }

    public isAcwAdmin(): boolean {
        return this.tokenService.hasPermissions(["acwadmin"]);
    }

    public isAcwUser(): boolean {
        return this.tokenService.hasPermissions(["acw"]);
    }

    public logout() {
        this.loaderService.showLoader();
        const state = crypto.randomUUID();
        this.sessionStorageService.setItem(SessionStorageKeys.PostLogoutState, state);
        this.sessionStorageService.clearAcwState();
        this.oauthService.logOut(false, state);
    }
}
